// Year-level interpretation of an ABSOLUTE NAPLAN mark — the second layer of
// the real NAPLAN architecture. Marking is year-independent (one rubric, same
// piece → same /47 at any age, see prompts/mark-writing.ts); what changes with
// the year is what a given total MEANS. This table is Coach Pen's editorial
// calibration of "typical for Year N" — the official raw→scaled mapping is not
// public — so the UI labels it as our reading, never as an official NAPLAN
// report. Labels mirror the real post-2023 proficiency level names. Tune the
// thresholds once PostHog has real score distributions.
type NaplanProficiency = "exceeding" | "strong" | "developing" | "support";

export const NAPLAN_YEARS = [3, 5, 7, 9] as const;
export type NaplanReportYear = (typeof NAPLAN_YEARS)[number];

// Minimum total (out of 47) for each level, per year. Monotone in both
// directions: higher years demand more for the same level.
const THRESHOLDS: Record<
  NaplanReportYear,
  { developing: number; strong: number; exceeding: number }
> = {
  3: { developing: 10, strong: 18, exceeding: 27 },
  5: { developing: 14, strong: 23, exceeding: 32 },
  7: { developing: 18, strong: 28, exceeding: 37 },
  9: { developing: 22, strong: 32, exceeding: 41 },
};

const LABELS: Record<NaplanProficiency, string> = {
  exceeding: "Exceeding",
  strong: "Strong",
  developing: "Developing",
  support: "Needs more support",
};

// Snap an arbitrary school year to the nearest NAPLAN reporting year (ties
// round up — a Year 4 writer is read against Year 5, the test they sit next).
export const nearestNaplanYear = (year: number): NaplanReportYear => {
  let best: NaplanReportYear = NAPLAN_YEARS[0];
  let bestDist = Infinity;
  for (const y of NAPLAN_YEARS) {
    const dist = Math.abs(y - year);
    if (dist < bestDist || (dist === bestDist && y > best)) {
      best = y;
      bestDist = dist;
    }
  }
  return best;
};

export interface NaplanProficiencyReading {
  level: NaplanProficiency;
  label: string;
  /** Chip text, e.g. "Strong for Year 5". */
  display: string;
  year: NaplanReportYear;
}

export function proficiencyFor(total: number, year: number): NaplanProficiencyReading {
  const y = nearestNaplanYear(year);
  const t = THRESHOLDS[y];
  const level: NaplanProficiency =
    total >= t.exceeding
      ? "exceeding"
      : total >= t.strong
        ? "strong"
        : total >= t.developing
          ? "developing"
          : "support";
  return { level, label: LABELS[level], display: `${LABELS[level]} for Year ${y}`, year: y };
}
